import { ApolloClient, InMemoryCache, createHttpLink, from } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { getFullPath } from './utils/pathUtils';

const handleLogout = async (message: string, client?: ApolloClient<any>): Promise<never> => {
  localStorage.removeItem('token');

  if (client) {
    try {
      await client.clearStore();
    } catch (error) {
      console.error('Error clearing Apollo cache:', error);
    }
  }

  sessionStorage.setItem('authError', message);

  window.location.href = getFullPath('/login');
  return new Promise(() => {});
};

const backendHost = process.env.REACT_APP_BACKEND_HOST || 'http://localhost:4000';

// Create the http link
const httpLink = createHttpLink({
  uri: `${backendHost}/graphql`,
});

// Set up the auth context link
const authLink = setContext((_, { headers }) => {
  // Get the token from localStorage
  const token = localStorage.getItem('token');

  // Return the headers to the context
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

let apolloClient: ApolloClient<any> | undefined = undefined;

// Set up error handling
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      const errorMessage = err.message.toLowerCase();
      // Only a missing/invalid session logs the user out. Prefer the coded
      // UNAUTHENTICATED error; fall back to legacy message matching. A FORBIDDEN
      // error means "authenticated but not allowed for this resource" and must
      // NOT log the user out.
      if (
        err.extensions?.code === 'UNAUTHENTICATED' ||
        errorMessage.includes('jwt expired') ||
        errorMessage === 'not authenticated' ||
        errorMessage.includes('not authenticated') ||
        errorMessage.includes('authentication required')
      ) {
        handleLogout('Your session has expired. Please log in again.', apolloClient);
        return;
      }
    }
  }

  if (networkError && 'statusCode' in networkError && networkError.statusCode === 401) {
    handleLogout('You need to log in to access this resource.', apolloClient);
  }
});

export const client = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache(),
});

apolloClient = client;
